"use client";

import React, { useState } from "react";
import { cn, paletteIndex } from "../utils";

export interface UserIconProps {
  name: string;
  /** Used for stable color hashing when available (falls back to name). */
  email?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-red-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-teal-500",
  "bg-orange-500",
  "bg-cyan-500",
] as const;

const SIZE_CLASSES = {
  xs: "w-5 h-5 text-[9px]",
  sm: "w-6 h-6 text-[10px]",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-base",
} as const;

const SIZE_PX = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
} as const;

function stringToColor(str: string): string {
  return AVATAR_COLORS[paletteIndex(str, AVATAR_COLORS.length)]!;
}

function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/** Derive initials from an email address (e.g. "jane.doe@acme.com" → "JD"). */
function getEmailInitials(email: string): string {
  const local = email.split("@")[0] ?? "";
  return getInitials(local);
}

export function UserIcon({
  name,
  email,
  avatarUrl,
  size = "md",
  className,
}: UserIconProps): React.ReactElement {
  const [imageError, setImageError] = useState(false);

  const safeName = name || "User";
  const hashKey = email ?? safeName;
  const bgColor = stringToColor(hashKey);
  const initials = email && !name.includes(" ") ? getEmailInitials(email) : getInitials(safeName);

  const hasValidAvatarUrl =
    avatarUrl && typeof avatarUrl === "string" && avatarUrl.startsWith("http") && !imageError;

  if (hasValidAvatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={safeName}
        width={SIZE_PX[size]}
        height={SIZE_PX[size]}
        className={cn("rounded-full object-cover shrink-0", SIZE_CLASSES[size], className)}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-medium text-white shrink-0",
        SIZE_CLASSES[size],
        bgColor,
        className,
      )}
    >
      {initials}
    </div>
  );
}
